import { Empty, Flex, Input, Skeleton, Table, Typography } from "antd";
import type { ColumnsType } from "antd/es/table";
import { useMemo, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import ClinicianAvatarCell from "../../components/Compliance/ClinicianAvatarCell.component";
import ComplianceDashboardHeader from "../../components/Compliance/ComplianceDashboardHeader.component";
import ComplianceTabs from "../../components/Compliance/ComplianceTabs.component";
import { TONE } from "../../components/Compliance/findings.shared";
import { useAppSelector } from "../../hooks/redux-hooks";
import { type ComplianceCoachingRow, useGetComplianceCliniciansQuery } from "../../service/compliance.service";
import styles from "./compliance.module.scss";

const trendColor: Record<string, string> = {
  declining: TONE.danger.fg,
  stable: TONE.neutral.accent,
  improving: TONE.success.fg,
};

const CliniciansList = () => {
  const navigate = useNavigate();
  const { userInfo } = useAppSelector((state) => state.auth);
  const [sort, setSort] = useState<string>("issue_count");
  const [search, setSearch] = useState<string>("");
  const [searchParams] = useSearchParams();
  // Demo affordance: hit /compliance/clinicians?empty=1 to preview the
  // "no clinicians yet" empty state.
  const forceEmpty = searchParams.get("empty") === "1";

  const { data, isFetching } = useGetComplianceCliniciansQuery({ sort, search, days: 30 });

  const rows = forceEmpty ? [] : data?.clinicians ?? [];

  const columns: ColumnsType<ComplianceCoachingRow> = useMemo(
    () => [
      {
        title: "Clinician",
        dataIndex: "display_name",
        key: "clinician",
        sorter: true,
        render: (_, row) => (
          <ClinicianAvatarCell
            name={row.display_name}
            initials={row.avatar_initials}
            subtitle={row.specialty}
            avatarSize={36}
          />
        ),
      },
      {
        title: "Notes reviewed",
        dataIndex: "notes_reviewed",
        key: "notes_reviewed",
        width: 140,
        render: (value: number | undefined) => (
          <Typography.Text>{typeof value === "number" ? value : 0}</Typography.Text>
        ),
      },
      {
        title: "Capture rate",
        dataIndex: "capture_rate",
        key: "capture_rate",
        width: 140,
        sorter: true,
        render: (value: number | undefined) =>
          typeof value === "number" ? (
            <Typography.Text strong>{value}%</Typography.Text>
          ) : (
            <Typography.Text style={{ color: "#9ca3af" }}>—</Typography.Text>
          ),
      },
      {
        title: "Issue count",
        dataIndex: "issue_count",
        key: "issue_count",
        width: 130,
        sorter: true,
        render: (value: number | undefined) =>
          typeof value === "number" ? (
            <Typography.Text strong>{value}</Typography.Text>
          ) : (
            <Typography.Text style={{ color: "#9ca3af" }}>—</Typography.Text>
          ),
      },
      {
        title: "Trend",
        dataIndex: "trend_label",
        key: "trend_label",
        width: 130,
        render: (value: string, row) =>
          value ? (
            <Typography.Text
              style={{ color: trendColor[value] || "#6b7280", fontWeight: 500, textTransform: "capitalize" }}
            >
              {value}
              {typeof row.trend_delta_pts === "number"
                ? ` ${row.trend_delta_pts > 0 ? "+" : ""}${row.trend_delta_pts}`
                : ""}
            </Typography.Text>
          ) : (
            <Typography.Text style={{ color: "#9ca3af" }}>—</Typography.Text>
          ),
      },
    ],
    [],
  );

  if (!userInfo?.show_compliance_dashboard || !userInfo?.is_group_admin) {
    return <Navigate to="/home" replace />;
  }

  return (
    <div className={styles.compliance__container}>
      <ComplianceDashboardHeader />

      <ComplianceTabs />

      <Flex gap={12} style={{ marginBottom: 16 }}>
        <Input.Search
          placeholder="Filter by name or email"
          allowClear
          style={{ maxWidth: 360 }}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </Flex>

      {isFetching && !data ? (
        <Skeleton active title={{ width: "30%" }} paragraph={{ rows: 8 }} style={{ padding: "8px 0" }} />
      ) : (
        <Table
          columns={columns}
          dataSource={rows}
          rowKey="clinician_id"
          scroll={{ x: 900 }}
          onChange={(_pag, _filters, sorter) => {
            const s = Array.isArray(sorter) ? sorter[0] : sorter;
            if (s && s.field && s.order) {
              const map: Record<string, string> = {
                clinician: "name",
                capture_rate: "capture_rate",
                issue_count: "issue_count",
              };
              setSort(map[String(s.field)] || "issue_count");
            }
          }}
          onRow={(row) => ({
            onClick: () => navigate(`/compliance/clinicians/${row.clinician_id}`),
            style: { cursor: "pointer" },
          })}
          pagination={{ pageSize: 25, hideOnSinglePage: true }}
          locale={{
            emptyText: (
              <Empty
                description={
                  <Flex vertical align="center" gap={4}>
                    <Typography.Text>No clinicians in this Group yet</Typography.Text>
                    <Typography.Text style={{ color: "#9ca3af", fontSize: 12 }}>
                      Invite clinicians from Manage Members to start tracking their notes here.
                    </Typography.Text>
                  </Flex>
                }
              />
            ),
          }}
        />
      )}
    </div>
  );
};

export default CliniciansList;
